import axios from 'axios';
import { normalizeApiError } from './errorUtils';
import { startGlobalLoader, stopGlobalLoader } from '../utils/globalLoaderBus';

const normalizeBaseUrl = (url) => {
  if (!url) return 'https://api.skhool.in/';
  return url.endsWith('/') ? url : `${url}/`;
};

const resolveBaseUrl = () => {
  const configured = normalizeBaseUrl(process.env.REACT_APP_API_BASE_URL);

  if (typeof window === 'undefined') return configured;

  const appHost = String(window.location?.hostname || '').toLowerCase();
  const isAppLocal = appHost === 'localhost' || appHost === '127.0.0.1';
  const isConfiguredLocal =
    configured.includes('://localhost:') ||
    configured.includes('://127.0.0.1:') ||
    configured.startsWith('http://localhost') ||
    configured.startsWith('http://127.0.0.1');

  if (!isAppLocal && isConfiguredLocal) {
    return 'https://api.skhool.in/';
  }

  return configured;
};

const BASE_URL = resolveBaseUrl();

const axiosPublic = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000,
});

const emitGlobalApiError = (normalizedError) => {
  if (typeof window === 'undefined' || !normalizedError) return;
  window.dispatchEvent(new CustomEvent('app:api-error', { detail: normalizedError }));
};

axiosPublic.interceptors.request.use(
  (config) => {
    const requestUrl = String(config?.url || '');
    const requestMethod = String(config?.method || 'get').toLowerCase();
    const isPublicPageFetch =
      requestMethod === 'get' &&
      /^\/api\/[^/]+\/pages(\/|$)/.test(requestUrl);

    if (isPublicPageFetch && config.skipGlobalError === undefined) {
      config.skipGlobalError = true;
    }

    if (!config.skipGlobalLoader) {
      config.__globalLoaderTracked = true;
      startGlobalLoader();
    }
    return config;
  },
  (error) => {
    if (error?.config?.__globalLoaderTracked) {
      stopGlobalLoader();
    }
    return Promise.reject(error);
  }
);

axiosPublic.interceptors.response.use(
  (response) => {
    if (response?.config?.__globalLoaderTracked) {
      stopGlobalLoader();
    }
    return response;
  },
  (error) => {
    if (error?.config?.__globalLoaderTracked) {
      stopGlobalLoader();
    }

    if (error.response && (error.response.status === 302 || error.response.status === 308)) {
      const redirectUrl = error.response.headers.location;
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return new Promise(() => {});
      }
    }

    const normalizedError = normalizeApiError(error, 'Unable to process your request.');
    error.normalizedError = normalizedError;

    if (!error.config?.skipGlobalError) {
      emitGlobalApiError(normalizedError);
    }

    return Promise.reject(error);
  }
);

axiosPublic.uploadImage = async (file, imageType = 'IMAGES', categoryName = null) => {
  if (!file) {
    throw new Error('File is required for upload.');
  }

  const formData = new FormData();
  formData.append('image', file);
  const resolvedCategoryName =
    categoryName || (imageType && imageType !== 'IMAGES' ? String(imageType) : 'General');
  formData.append('categoryName', resolvedCategoryName);

  try {
    const response = await axiosPublic.post('/api/images/upload/by-category', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const payload = response.data;
    return payload?.fileUrl || payload?.imageUrl || payload?.url || payload;
  } catch (error) {
    const normalizedError = error.normalizedError || normalizeApiError(error, 'Image upload failed.');
    throw new Error(normalizedError.message);
  }
};

axiosPublic.getSignedUrl = async (fileUrlOrKey, expiresInMinutes = 60) => {
  if (!fileUrlOrKey) return null;
  try {
    const response = await axiosPublic.get('/api/images/signed-url', {
      params: { file: fileUrlOrKey, expiresInMinutes },
      skipGlobalLoader: true,
      skipGlobalError: true,
    });
    return response?.data?.signedUrl || null;
  } catch (error) {
    return null;
  }
};

export default axiosPublic;
